import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import CameraTagChip from '../CameraTagChip';
import { Callout } from '../../common/Callout';
import { tagErrorMessage, type CameraTag, type CameraTagCategory, type CameraTagsApi } from './cameraTagsApi';
import { refreshCameraTagCatalog } from './useCameraTagCatalog';

/** The bulk endpoint takes at most 2000 cameras per call. */
const ASSIGN_CHUNK = 2000;

async function assignInChunks(api: CameraTagsApi, ids: string[], add: string[], remove: string[]) {
  for (let i = 0; i < ids.length; i += ASSIGN_CHUNK) {
    await api.bulkAssign(ids.slice(i, i + ASSIGN_CHUNK), add, remove);
  }
}

export interface TagCamerasModalProps {
  api: CameraTagsApi;
  isOpen: boolean;
  onClose: () => void;
  tag: CameraTag | null;
  category: CameraTagCategory;
}

/**
 * Tag-first assignment: open a tag, tick the cameras that belong to it, save. The same job as
 * the camera list's bulk "Tags…", from the other end — "these 40 cameras are Traffic junction
 * cameras" without paging through the camera list.
 *
 * Starts with the cameras that already carry the tag ticked; Save applies only the difference.
 */
export default function TagCamerasModal({ api, isOpen, onClose, tag, category }: TagCamerasModalProps) {
  const [cameras, setCameras] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initial, setInitial] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState('');
  const [show, setShow] = useState<'all' | 'tagged' | 'untagged'>('all');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !tag) return;
    setLoading(true);
    setError('');
    setSearch('');
    setGroupId('');
    setShow('all');
    api.listAllCameras()
      .then((list) => {
        list.sort((a: any, b: any) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
        const has = new Set<string>(list.filter((c: any) => (c.tags || []).some((t: any) => t.id === tag.id)).map((c: any) => c.id as string));
        setCameras(list);
        setInitial(has);
        setChecked(new Set(has));
      })
      .catch((err) => setError(tagErrorMessage(err, 'Could not load cameras')))
      .finally(() => setLoading(false));
  }, [isOpen, tag?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Groups to filter by — the ones the cameras are actually in. */
  const groups = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cameras) for (const g of (c.groups || []) as any[]) map.set(g.id, g.name);
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [cameras]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cameras.filter((c) => {
      if (q && ![c.name, c.ip_address, c.location].some((v) => String(v || '').toLowerCase().includes(q))) return false;
      if (groupId && !(c.groups || []).some((g: any) => g.id === groupId)) return false;
      if (show === 'tagged' && !checked.has(c.id)) return false;
      if (show === 'untagged' && checked.has(c.id)) return false;
      return true;
    });
  }, [cameras, search, groupId, show, checked]);

  if (!isOpen || !tag) return null;

  const allVisibleChecked = visible.length > 0 && visible.every((c) => checked.has(c.id));

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleVisible() {
    setChecked((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) visible.forEach((c) => next.delete(c.id));
      else visible.forEach((c) => next.add(c.id));
      return next;
    });
  }

  const added = [...checked].filter((id) => !initial.has(id));
  const removed = [...initial].filter((id) => !checked.has(id));

  /** Other tags of this category on a camera — what a one-per-camera add will replace. */
  const siblingsOf = (c: any): any[] => (c.tags || []).filter((t: any) => t.category_id === category.id && t.id !== tag.id);
  const addedSet = new Set(added);
  const moving = category.multi_select ? 0 : cameras.filter((c) => addedSet.has(c.id) && siblingsOf(c).length).length;

  async function save() {
    setSaving(true);
    setError('');
    try {
      if (added.length) await assignInChunks(api, added, [tag!.id], []);
      if (removed.length) await assignInChunks(api, removed, [], [tag!.id]);
      await refreshCameraTagCatalog(api);
      onClose();
    } catch (err) {
      setError(tagErrorMessage(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  }

  const selectCls = 'rounded-lg border border-border bg-input px-2.5 py-1.5 text-[13px] text-foreground focus:border-brand focus:outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex h-[85vh] w-full max-w-2xl flex-col rounded-xl border border-border bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 text-[16px] font-semibold text-foreground">
              Cameras tagged <CameraTagChip name={tag.name} prefix={category.name} color={tag.color} />
            </h2>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Tick every camera that should carry this tag. {checked.size} of {cameras.length} ticked.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Narrowing */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
          <div className="relative min-w-[12rem] flex-1">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, IP or location…"
              className="w-full rounded-lg border border-border bg-input py-1.5 pl-8 pr-3 text-[13px] text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none"
            />
          </div>
          {groups.length > 0 && (
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={selectCls} aria-label="Group">
              <option value="">All groups</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          )}
          <select value={show} onChange={(e) => setShow(e.target.value as any)} className={selectCls} aria-label="Show">
            <option value="all">All cameras</option>
            <option value="tagged">Ticked</option>
            <option value="untagged">Not ticked</option>
          </select>
        </div>

        {/* Select-all for whatever the narrowing shows */}
        <label className="flex cursor-pointer items-center gap-3 border-b border-border bg-muted px-5 py-2 text-[12px] font-medium text-foreground">
          <input
            type="checkbox"
            checked={allVisibleChecked}
            onChange={toggleVisible}
            disabled={!visible.length}
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
          />
          {allVisibleChecked ? 'Untick' : 'Tick'} all {visible.length} shown
          {(search || groupId || show !== 'all') && <span className="font-normal text-muted-foreground">(filtered)</span>}
        </label>

        {/* Cameras */}
        <div className="flex-1 overflow-y-auto">
          {loading && <p className="px-5 py-6 text-[13px] text-muted-foreground">Loading cameras…</p>}
          {!loading && !visible.length && !error && (
            <p className="px-5 py-6 text-[13px] text-muted-foreground">No cameras match.</p>
          )}
          {!loading && visible.map((c) => {
            const on = checked.has(c.id);
            const changed = on !== initial.has(c.id);
            const siblings = siblingsOf(c);
            return (
              <label
                key={c.id}
                className={`flex cursor-pointer items-center gap-3 border-b border-border px-5 py-2.5 hover:bg-muted ${changed ? 'bg-brand-light' : ''}`}
              >
                <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="h-4 w-4 flex-shrink-0 rounded border-border text-brand focus:ring-brand" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-foreground">{c.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {[c.ip_address, c.location, ...(c.groups || []).slice(0, 2).map((g: any) => g.name)].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <div className="flex flex-shrink-0 flex-wrap justify-end gap-1">
                  {siblings.map((t: any) => (
                    <span key={t.id} className={on && !category.multi_select ? 'line-through opacity-60' : ''}>
                      <CameraTagChip name={t.name} color={t.color} />
                    </span>
                  ))}
                  {changed && (
                    <span className="text-[11px] font-semibold text-brand">{on ? 'adding' : 'removing'}</span>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className="space-y-2 border-t border-border px-5 py-4">
          {error && <Callout tone="danger">{error}</Callout>}
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] text-muted-foreground">
              {added.length || removed.length ? (
                <>
                  {added.length > 0 && <span className="font-medium text-foreground">+{added.length} to tag</span>}
                  {added.length > 0 && removed.length > 0 && ', '}
                  {removed.length > 0 && <span className="font-medium text-foreground">−{removed.length} to untag</span>}
                  {moving > 0 && <span> · {moving} will lose their other {category.name.toLowerCase()} tag (one per camera)</span>}
                </>
              ) : 'No changes yet'}
            </p>
            <div className="flex gap-2">
              <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || loading || (!added.length && !removed.length)}
                className="rounded-lg bg-brand px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
